import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { BaseWeapon, BaseProjectile } from './BaseWeapon';
import { GameConfig } from '../config/GameConfig';

export class Fish extends BaseProjectile {
  private target?: Enemy;
  private enemies: Phaser.Physics.Arcade.Group;
  private homingSpeed: number = GameConfig.WEAPON_DATA.FISH.HOMING_SPEED;

  constructor(scene: Phaser.Scene, x: number, y: number, enemies: Phaser.Physics.Arcade.Group, damage: number) {
    super(scene, x, y, '');
    this.enemies = enemies;
    this.createFishTexture(scene);
    this.setTexture('projectile-fish');
  }

  public activate(x: number, y: number, damage: number, lifespan: number): void {
    this.setPosition(x, y);
    this.damage = damage;
    this.findTarget();

    this.setActive(true);
    this.setVisible(true);

    this.scene.time.delayedCall(lifespan, () => {
      if (this.active) {
        this.setActive(false);
        this.setVisible(false);
        this.setVelocity(0, 0);
      }
    });
  }

  private createFishTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists('projectile-fish')) {
      return;
    }
    const graphics = scene.add.graphics();
    const size = 16;
    const halfSize = size / 2;

    // Fish body (blue-gray)
    graphics.fillStyle(0x4A90E2, 1);
    graphics.fillEllipse(halfSize, halfSize, 7, 4);

    // Fish tail
    graphics.fillStyle(0x357ABD, 1);
    graphics.fillTriangle(
      halfSize - 5, halfSize,
      halfSize - 8, halfSize - 3,
      halfSize - 8, halfSize + 3
    );

    // Eye
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(halfSize + 2, halfSize - 1, 1);

    // Highlight
    graphics.fillStyle(0x6DB3F2, 1);
    graphics.fillEllipse(halfSize + 1, halfSize - 1, 3, 2);

    graphics.generateTexture('projectile-fish', size, size);
    graphics.destroy();
  }

  private findTarget(): void {
    const enemyArray = this.enemies.getChildren() as Enemy[];
    if (enemyArray.length === 0) return;

    let nearest: Enemy | null = null;
    let minDistance = Infinity;

    for (const enemy of enemyArray) {
      if (!enemy.active) continue;

      const distance = Phaser.Math.Distance.Between(
        this.x, this.y,
        enemy.x, enemy.y
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearest = enemy;
      }
    }

    this.target = nearest || undefined;
  }

  update(): void {
    if (!this.active) return;

    // Re-target if current target is dead
    if (!this.target || !this.target.active) {
      this.findTarget();
    }

    if (this.target) {
      // Home in on target
      const direction = new Phaser.Math.Vector2(
        this.target.x - this.x,
        this.target.y - this.y
      );

      direction.normalize();
      direction.scale(this.homingSpeed);

      this.setVelocity(direction.x, direction.y);

      // Rotate to face direction
      this.rotation = Math.atan2(direction.y, direction.x);
    }
  }
}

export class FishWeapon extends BaseWeapon {
  private attackDelay: number = GameConfig.WEAPON_DATA.FISH.COOLDOWN;
  private fishCount: number = 1;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    super(scene, player, enemies, Fish);
    this.startAutoAttack();

    this.projectiles.createMultiple({
      classType: Fish,
      key: 'projectile-fish',
      repeat: 10 - 1,
      active: false,
      visible: false,
    });
  }

  protected startAutoAttack(): void {
    this.attackTimer = this.scene.time.addEvent({
      delay: this.attackDelay,
      callback: this.attack,
      callbackScope: this,
      loop: true
    });
  }

  protected attack(): void {
    for (let i = 0; i < this.fishCount; i++) {
      // Slight delay between multiple fish
      this.scene.time.delayedCall(i * 100, () => {
        const fish = this.projectiles.get(this.player.x, this.player.y) as Fish;
        if (fish) {
          fish.activate(
            this.player.x,
            this.player.y,
            GameConfig.WEAPON_DATA.FISH.DAMAGE * this.damageMultiplier,
            GameConfig.WEAPON_DATA.FISH.LIFESPAN
          );
        }
      });
    }
  }

  public upgradeDamage(amount: number): void {
    this.damageMultiplier += amount;
  }

  public upgradeSpeed(): void {
    this.attackDelay = Math.max(500, this.attackDelay * GameConfig.WEAPON_DATA.FISH.COOLDOWN_UPGRADE);
    if (this.attackTimer) {
      this.attackTimer.destroy();
    }
    this.startAutoAttack();
  }

  public addFish(): void {
    this.fishCount++;
  }
}
